import type { JobProfileSalaryMatch } from 'src/graphql/graphql';

import type {
  Recommendations,
  JobDetails,
  RecommendedGlobalJob,
  Viewer,
} from './queries/types';
import { apacSavedSearches } from './savedSearchMock.ts';

export const SOL_TOKEN = '1~5c5623c0-3996-4dc8-82a0-fe8b1b450963';

const globalJobs = new Array(5).fill(1);
const titles = ['Designer', 'Developer', 'Director', 'Cook'];
const adjectives = [
  'Super-Duper Good',
  'Senior Non-executive',
  'Awesome',
  'Legendary',
];

export const baseGlobalJob: RecommendedGlobalJob = {
  __typename: 'RecommendedGlobalJob',
  isNew: false,
  isSaved: false,
  solMetadata: {},
  job: {
    __typename: 'Job',
    id: '123456',
    advertiser: {
      __typename: 'Advertiser',
      id: '1',
      name: 'AMP Services Limited',
    },
    title: 'Business Analysts',
    abstract:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    classifications: [
      {
        __typename: 'ClassificationInfo',
        label:
          'Business/Systems Analysts (Information & Communication Technology)',
      },
    ],
    products: {
      __typename: 'JobProducts',
      bullets: [],
      branding: {
        __typename: 'JobProductBranding',
        logo: {
          __typename: 'JobProductBrandingImage',
          url: 'http://image-service-cdn.cloud.seek.com.au/55da304f4ca3647cdf68fc4329dfdc087a280c42/serp-logo',
        },
      },
    },
    salary: {
      __typename: 'JobSalary',
      currencyLabel: null,
      label: '$65k - $75k p.a.',
    },
    location: {
      __typename: 'LocationInfo',
      label: 'Richmond, Melbourne VIC',
    },
    createdAt: {
      __typename: 'SeekDateTime',
      label: '3d ago',
    },
    listedAt: {
      __typename: 'SeekDateTime',
      label: '3d ago',
    },
  },
};

const mapToGlobalRecJobs = (): RecommendedGlobalJob[] =>
  globalJobs.map((_, index) => {
    const jobId = String(123456 + index);
    return {
      __typename: 'RecommendedGlobalJob',
      isNew: Boolean(index === 0),
      isSaved: Boolean(index % 2 === 0),
      solMetadata: {
        jobId,
        token: SOL_TOKEN,
        tags: {
          'recs:recSysChannel': '',
          testRecord: 'false',
          'recs:experimentGroup': '183',
          'recs:flight': '183',
          'recs:product': 'recommended_jobs_page',
        },
      },
      job: {
        ...baseGlobalJob.job,
        id: jobId,
        title: `${adjectives[index % 4]} ${titles[index % 4]} ${index + 1}`,
        products: Boolean(index % 3 === 0)
          ? baseGlobalJob.job.products
          : {
              __typename: 'JobProducts',
              bullets: [],
              branding: null,
            },
        salary: Boolean(index === 1)
          ? {
              __typename: 'JobSalary',
              currencyLabel: 'NZD',
              label: '$65k - $75k p.a.',
            }
          : baseGlobalJob.job.salary,
      },
    };
  });

export const recommendations: Recommendations = {
  __typename: 'Recommendations',
  recommendedGlobalJobs: {
    __typename: 'RecommendedGlobalJobs',
    globalJobs: mapToGlobalRecJobs(),
    solMetadata: {
      token: SOL_TOKEN,
      tags: {
        'recs:product': 'recommended_jobs_page',
      },
    },
  },
};

export const getCandidate = (): Viewer => ({
  __typename: 'Candidate',
  personalDetails: {
    __typename: 'PersonalDetails',
    firstName: 'Joe',
  },
  emailAddress: 'joe@gmail.com',
  trackingId: '2cef3f488f07f1e946cd6a6bdcc62bf9',
  id: 39675318,
  recommendations,
  apacSavedSearches,
});

const jobDetailsMock: Record<string, any> = {
  '123456': {
    title: 'Super-Duper Good Designer 1',
    workType: 'Full time',
    classifications: [
      {
        __typename: 'ClassificationInfo',
        label:
          'Business/Systems Analysts (Information & Communication Technology)',
      },
      {
        __typename: 'ClassificationInfo',
        label: 'Business Services & Corporate Advisory (Accounting)',
      },
    ],
    personalised: {
      __typename: 'PersonalisedJobDetails',
      appliedDateTime: null,
      salaryMatch: {
        __typename: 'JobProfileMissingSalaryPreference',
        label: 'Missing Salary Preference',
      },
      topApplicantBadge: {
        __typename: 'PersonalisedJobDetails',
        label: 'You may be a strong applicant',
        description:
          'Using data based on employers’ past behaviour, the default resumé in your profile and other factors, SEEK predicts you may be a strong applicant for this role.',
      },
      topApplicantBadge2: {
        __typename: 'PersonalisedJobDetails',
        label: 'You may be a strong applicant',
        description:
          'Using data based on employers’ past behaviour, the default resumé in your profile and other factors, SEEK predicts you may be a strong applicant for this role.',
      },
    },
    companyProfile: {
      id: '1234',
      name: 'AMP Services Limited',
      companyNameSlug: 'amp-services-1234',
      shouldDisplayReviews: true,
      branding: {
        logo: 'http://image-service-cdn.cloud.seek.com.au/55da304f4ca3647cdf68fc4329dfdc087a280c42/serp-logo',
      },
      overview: {
        description: {
          paragraphs: [
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex',
          ],
        },
        industry:
          'Business/Systems Analysts (Information & Communication Technology)',
        size: {
          description: 'More than 10,000 employees',
        },
      },
      reviewsSummary: {
        overallRating: {
          numberOfReviews: {
            value: 86,
          },
          value: 3.4,
        },
      },
      perksAndBenefits: [
        {
          title: 'Flexible working',
        },
        {
          title: 'Financial wellbeing',
        },
        {
          title: 'Health & lifestyle',
        },
        {
          title: 'Community',
        },
      ],
    },
  },
  '123457': {
    title: 'Senior Non-executive Developer 2',
    workType: 'Full time',
    companyProfile: {
      id: '1234',
      name: 'AMP Services Limited',
      companyNameSlug: 'amp-services-1234',
      shouldDisplayReviews: true,
      branding: {
        logo: null,
      },
      overview: {
        description: {
          paragraphs: [
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex',
          ],
        },
        industry:
          'Business/Systems Analysts (Information & Communication Technology)',
        size: {
          description: 'More than 10,000 employees',
        },
      },
      reviewsSummary: {
        overallRating: {
          numberOfReviews: {
            value: 86,
          },
          value: 3.4,
        },
      },
      perksAndBenefits: [],
    },
    personalised: {
      __typename: 'PersonalisedJobDetails',
      appliedDateTime: null,
      salaryMatch: {
        __typename: 'JobProfileSalaryNoMatch',
        label: 'Not Match',
      },
      topApplicantBadge: {
        __typename: 'PersonalisedJobDetails',
        label: 'You may be a strong applicant',
        description:
          'Using data based on employers’ past behaviour, the default resumé in your profile and other factors, SEEK predicts you may be a strong applicant for this role.',
      },
      topApplicantBadge2: {
        __typename: 'PersonalisedJobDetails',
        label: 'You may be a strong applicant',
        description:
          'Using data based on employers’ past behaviour, the default resumé in your profile and other factors, SEEK predicts you may be a strong applicant for this role.',
      },
    },
  },
  '123458': {
    title: 'Awesome Director 3 (Aggregated JobAd)',
    workType: 'Part time',
    isVerified: false,
    companyProfile: {
      id: '1234',
      name: 'AMP Services Limited',
      companyNameSlug: 'amp-services-1234',
      shouldDisplayReviews: false,
      branding: {
        logo: 'http://image-service-cdn.cloud.seek.com.au/55da304f4ca3647cdf68fc4329dfdc087a280c42/serp-logo',
      },
      overview: {
        industry:
          'Business/Systems Analysts (Information & Communication Technology)',
      },
      reviewsSummary: null,
      perksAndBenefits: [
        {
          title: 'Flexible working',
        },
        {
          title: 'Financial wellbeing',
        },
        {
          title: 'Health & lifestyle',
        },
        {
          title: 'Community',
        },
      ],
    },
    products: {
      displayTags: [
        {
          label: 'Immediate Start',
          __typename: 'UrgentJobDisplayTag',
        },
      ],
    },
  },
  '123459': {
    title: 'Legendary Cook 4',
    workType: 'Full time',
    personalised: {
      salaryMatch: {
        __typename: 'JobProfileSalaryMatch',
        label: 'Match',
        salaryPreference: {
          id: '0df684cb-2cfb-4f22-96a7-12e4d3682d8f',
          description: 'AUD 100,000 annual',
          country: {
            countryCode: 'AU',
            name: 'Australia',
          },
          currencyCode: 'AUD',
          amount: 100000,
          salaryType:
            'Annual' as JobProfileSalaryMatch['salaryPreference']['salaryType'],
        },
      },
    },
    '123460': {
      title: 'Super-Duper Good Designer 5',
      workType: 'Full time',
    },
  },
};

export const jobDetails = ({ id }: { id: string }): JobDetails => ({
  __typename: 'JobDetails',
  job: {
    tracking: {
      adProductType: 'Classic',
      classificationInfo: {
        classificationId: '1225',
        classification: 'Trades & Services',
        subClassificationId: '6245',
        subClassification: 'Other',
        __typename: 'JobTrackingClassificationInfo',
      },
      hasRoleRequirements: true,
      isPrivateAdvertiser: false,
      locationInfo: {
        area: 'CBD, Inner West & Eastern Suburbs',
        location: 'Sydney',
        locationIds: ['16351'],
        __typename: 'JobTrackingLocationInfo',
      },
      postedTime: '3d ago',
      workTypeIds: '242',
      __typename: 'JobTracking',
    },
    id,
    title: jobDetailsMock[id].title,
    phoneNumber: '0444555666',
    isExpired: false,
    isLinkOut: false,
    isVerified: jobDetailsMock[id]?.isVerified ?? true,
    contactMatches: [],
    content:
      '<p><strong>Our Business</strong></p><p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum</p><p><strong>Job Tasks / Responsibilities</strong></p><ul><li>Phasellus laoreet odio ac mi pulvinar euismod</li><li>Donec quis nulla eget erat hendrerit ullamcorper</li><li>Mauris vestibulum leo non risus rutrum gravida</li><li>Mauris vestibulum leo non risus rutrum gravida</li></ul><p><strong>Desired Skills / Experience</strong></p><ul><li>Lorem ipsum dolor sit amet, consectetur adipiscing elit</li><li>Etiam iaculis orci at dapibus ultricies</li><li>Donec a tellus non nulla posuere dapibus</li><li>Curabitur id purus dapibus elit sollicitudin fringilla eget nec leo</li></ul></p>',
    status: 'Active',
    listedAt: {
      label: '3d ago',
      __typename: 'SeekDateTime',
    },
    salary: {
      currencyLabel: id === '123457' ? 'NZD' : null,
      label: '$65k - $75k p.a.',
      __typename: 'JobSalary',
    },
    shareLink: `http://dev.seek.com.au:8000/job/${id}`,
    workTypes: {
      label: jobDetailsMock[id].workType ?? '',
      __typename: 'JobWorkTypes',
    },
    advertiser: {
      id: '39202174',
      name: 'AMP Services Limited',
      __typename: 'Advertiser',
    },
    location: {
      label: 'Melbourne • CBD & Inner Suburbs',
      __typename: 'LocationInfo',
    },
    classifications: jobDetailsMock[id].classifications
      ? jobDetailsMock[id].classifications
      : baseGlobalJob.job.classifications,
    products: {
      branding: null,
      bullets: null,
      questionnaire: {
        questions: [
          "How many years' experience do you have as a Customer Service Representative?",
          'Which of the following Microsoft Office products are you experienced with?',
        ],
        __typename: 'JobQuestionnaire',
      },
      video: null,
      __typename: 'JobProducts',
      displayTags: jobDetailsMock[id].products?.displayTags ?? [],
    },
    __typename: 'Job',
  },
  learningInsights: {
    analytics: {
      'skl-lmis': 'KeywordsSearchToRole:picker-and-packer',
      'skl-lmis-type': 'career',
    },
    content:
      '<style>\n  .EA097D-a * {\n    margin: 0;\n    padding: 0;\n    border: 0;\n    font: inherit;\n    vertical-align: baseline;\n    -webkit-font-smoothing: antialiased;\n  }\n\n  .EA097D-a *,\n  .EA097D-a li:before {\n    font-family: inherit;\n    box-sizing: inherit;\n    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);\n    -webkit-tap-highlight-color: transparent;\n  }\n\n  .EA097D-a {\n    font-family: \'Roboto\', \'Helvetica Neue\', HelveticaNeue, Helvetica, Arial, sans-serif;\n    font-size: 10px;\n    box-sizing: border-box;\n    min-height: 100%;\n    position: relative;\n  }\n\n  .EA097D-b {\n    font-size: 2.1em;\n    line-height: 1.28571429em;\n    -webkit-transform: translateY(0.26285714em);\n    transform: translateY(0.26285714em);\n    color: #1c1c1c;\n    text-decoration: none;\n    display: block;\n    margin-bottom: 18px;\n  }\n\n  .EA097D-d {\n    margin-bottom: 7px;\n    list-style: none;\n  }\n\n  .EA097D-d li {\n    font-size: 1.6em;\n    line-height: 1.28571429em;\n    -webkit-transform: translateY(0.26285714em);\n    transform: translateY(0.26285714em);\n  }\n\n  .EA097D-d li {\n    color: #1c1c1c;\n    margin-bottom: 9px;\n    margin-left: 17px;\n    list-style: disc;\n  }\n\n  .EA097D-d li:last-child {\n    margin-bottom: 30px;\n  }\n\n  .EA097D-f {\n    font-size: 1.8em;\n    line-height: 45px;\n    border-radius: 2px;\n    background: #fff;\n    width: 160px;\n    color: #2765cf;\n    text-decoration: none;\n    padding: 0 14px;\n    border: 2px solid #2765cf;\n    display: block;\n    text-align: center;\n  }\n\n  .EA097D-h {\n    height: 30px;\n    width: 170px;\n    margin-bottom: 0;\n    background: url(https://cdn.seeklearning.com.au/media/images/career-guide/career-advice-blue.svg) no-repeat center;\n    background-size: contain;\n  }\n\n  @media only screen and (min-width: 580px) {\n    .EA097D-h {\n      margin-bottom: 16px;\n    }\n  }\n</style>\n\n<div class="EA097D-a">\n  <div class="EA097D-h"></div>\n  <h1>\n    <a class="EA097D-b" href="https://www.seek.com.au/career-advice/role/picker-and-packer?campaigncode=lrn:skj:sklm:cg:jbd:alpha"\n      >Courses that get you job-ready</a\n    >\n  </h1>\n  <ul class="EA097D-d">\n    <li>Insights on salary & in-demand skills</li>\n    <li>Reviews from people working in the industry</li>\n  </ul>\n  <a class="EA097D-f" href="https://www.seek.com.au/career-advice/role/picker-and-packer?campaigncode=lrn:skj:sklm:cg:jbd:alpha">Explore career</a>\n</div>\n',
    __typename: 'LearningInsights',
  },
  companyProfile:
    (jobDetailsMock[id].companyProfile && {
      ...jobDetailsMock[id].companyProfile,
      __typename: 'CompanyProfile',
    }) ??
    null,
  companySearchUrl: null,
  companyTags: [
    { __typename: 'CompanyTag', key: 'Registration No.', value: '20153489G' },
    { __typename: 'CompanyTag', key: 'RF No.', value: '150-LB-090120-R' },
  ],
  restrictedApplication: {
    __typename: 'JobDetailsRestrictedApplication',
    label:
      id === '123457'
        ? 'This role is located outside the Philippines. You cannot apply because it does not meet Department of Migrant Workers requirements.'
        : null,
  },
  sourcr: null,
  personalised: jobDetailsMock[id].personalised || null,
});

export default () => ({
  Candidate: getCandidate,
  UpdateSavedJobPayload: (_: any, { id }: any) => ({ savedJobId: id }),
  JobDetails: jobDetails,
});
